import { useMemo } from 'react';
import type { QuestionBelongToAnotherClient } from 'rtk/LibrarySlice';
import { useGetQuestionByIdQuery } from 'rtk/query/apis/questions';
import { useAppSelector } from 'rtk/query/hooks';
import { type TAnyClientQuestionType, type TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import { selectTemplateOptions } from 'rtk/TemplateSelector';
import { applyTemplateOptionsToQuestion } from 'utils/questionTemplateOptions';

type TUseQuestion = {
  data: any;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: any;
  exists: boolean;
  missingLanguage: boolean;
  questionConfig: TAnyClientQuestionType | null | undefined;
  question: TAnyClientQuestionType | null | undefined;
  language?: TAvailableLanguageCodes;
  version: number;
  questionBelongToAnotherClient?: QuestionBelongToAnotherClient;
};

type TAppliedOptions = {
  templateOptions: unknown;
  lang: TAvailableLanguageCodes;
  result: ReturnType<typeof applyTemplateOptionsToQuestion>;
};

// Every useQuestion call site memoizes per hook instance, so N mounted consumers of the same
// question (e.g. a row per option) each redid this O(options) merge. Sharing the result per Library
// entry means it runs once per entry/template-options change instead of once per consumer.
const appliedOptionsCache = new WeakMap<object, TAppliedOptions>();

const applyTemplateOptionsOnce = (
  entry: object,
  qc: Parameters<typeof applyTemplateOptionsToQuestion>[0],
  templateOptions: Parameters<typeof applyTemplateOptionsToQuestion>[1],
  lang: TAvailableLanguageCodes,
) => {
  const cached = appliedOptionsCache.get(entry);
  if (cached && cached.templateOptions === templateOptions && cached.lang === lang) return cached.result;
  const result = applyTemplateOptionsToQuestion(qc, templateOptions, lang);
  appliedOptionsCache.set(entry, { templateOptions, lang, result });
  return result;
};

export function useQuestion(nodeId: string | null): TUseQuestion {
  const selectLibraryEntry = (state: any) => (nodeId ? state.Library[nodeId] : null);
  const entry = useAppSelector(selectLibraryEntry);

  const selectTemplateOptionsByNode = (state: any) => (nodeId ? selectTemplateOptions(state, nodeId) : undefined);
  const templateOptions = useAppSelector(selectTemplateOptionsByNode);

  const { data, isLoading, isFetching, isError, error } = useGetQuestionByIdQuery(
    { questionId: nodeId },
    { skip: !nodeId?.length || entry !== undefined },
  );

  const memoized = useMemo(() => {
    if (!entry) {
      return {
        questionBelongToAnotherClient: undefined as QuestionBelongToAnotherClient | undefined,
        questionConfig: undefined as TAnyClientQuestionType | undefined,
        language: undefined as TAvailableLanguageCodes | undefined,
        exists: false,
        missingLanguage: false,
        question: null as TAnyClientQuestionType | null,
        version: 0,
      };
    }
    const { questionBelongToAnotherClient, ...qc } = entry;
    const lang = qc.language?.c || 'en-gb';
    const questionConfig = applyTemplateOptionsOnce(entry, qc, templateOptions, lang);
    const language = questionConfig?.language?.c as TAvailableLanguageCodes | undefined;
    const exists = !!questionConfig && Object.keys(questionConfig).length > 0;
    let missingLanguage = false;
    let question: TAnyClientQuestionType | null = null;
    if (language && questionConfig.available_languages.includes(language)) {
      question = questionConfig;
    } else {
      missingLanguage = true;
    }
    return {
      questionBelongToAnotherClient,
      questionConfig,
      language,
      exists,
      missingLanguage,
      question,
      version: questionConfig?.version ?? 0,
    };
  }, [entry, templateOptions]);

  if (!memoized.exists || isLoading) {
    return {
      data,
      isLoading,
      isFetching,
      isError,
      error,
      exists: false,
      missingLanguage: false,
      questionConfig: null,
      question: null,
      language: undefined,
      version: 0,
      questionBelongToAnotherClient: undefined,
    };
  }

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    questionConfig: memoized.questionConfig,
    question: memoized.question,
    language: memoized.language,
    version: memoized.version,
    questionBelongToAnotherClient: memoized.questionBelongToAnotherClient,
    exists: true,
    missingLanguage: memoized.missingLanguage,
  };
}
